import {
  PurchaseApprovalLine,
  PurchaseApprovalPayload as SharedPurchaseApprovalPayload,
  PurchaseDeadlineSource,
  PurchaseInstrumentType,
  ResponseArtifactDTO,
} from '@dreamai/types';

export type {
  PurchaseApprovalLine,
  PurchaseDeadlineSource,
  PurchaseInstrumentType,
} from '@dreamai/types';

/**
 * Secure-payment artifacts (the private purchase approval card, the
 * CVV / bank-code forms and the live bank challenge) belong to the owning
 * user's own Dream chat. The approval card alone also travels as a no-login
 * `/artifact/:shareToken` link (sent to WhatsApp-rooted users) and is actioned
 * through the share-token secure-payments routes; the code forms and the live
 * challenge never do. These helpers recognise each kind from the artifact JSON
 * so the client renders — or refuses — accordingly.
 */

export type PaymentCredentialKind =
  | 'payment_cvv'
  | 'payment_3ds'
  | 'payment_3ds_live';

const PAYMENT_CREDENTIAL_KINDS: ReadonlySet<string> =
  new Set<PaymentCredentialKind>([
    'payment_cvv',
    'payment_3ds',
    'payment_3ds_live',
  ]);

export const PAYMENT_PRIVATE_ONLY_MESSAGE =
  'This payment step can only be completed in your own Dream chat. Open the Dream app to continue.';

/** Normalised instrument option: every display field is present (or null). */
export interface PurchaseInstrumentOption {
  type: PurchaseInstrumentType;
  /** Opaque credentialId / paymentInstrumentId — never card data. */
  ref: string | null;
  label: string | null;
  last4: string | null;
  network: string | null;
  /**
   * Saved cards only: whether the vault already holds the security code.
   * `false` / `null` means the approval card shows an inline code box for it.
   * Never the code itself.
   */
  cvcOnFile: boolean | null;
}

/** Digits a card network expects in its security code. */
export const securityCodeLength = (network: string | null): number =>
  network?.toLowerCase() === 'amex' ? 4 : 3;

/** True once the typed code is exactly the digits this card's network expects. */
export const isSecurityCodeComplete = (
  code: string,
  network: string | null,
): boolean => new RegExp(`^\\d{${securityCodeLength(network)}}$`).test(code);

/**
 * How the executor read the checkout total: an exact selector written for
 * that checkout, or a generic class-name heuristic the owner should check
 * against the screenshot. Null when the server did not say.
 */
export type PurchaseTotalSource = 'exact' | 'heuristic';

/** Validated, fully-normalised purchase payload the modal renders from. */
export interface PurchaseApprovalPayload
  extends Omit<
    SharedPurchaseApprovalPayload,
    'evidenceArtifactId' | 'instrumentOptions' | 'totalSource' | 'linesReadable'
  > {
  evidenceArtifactId: string | null;
  instrumentOptions: PurchaseInstrumentOption[];
  totalSource: PurchaseTotalSource | null;
  /** False when the basket lines could not be read and the single line was taken from the total. */
  linesReadable: boolean;
}

/** Owner-facing caveat when the amount is less than an exact read, else null. */
export const purchaseTotalCaveat = (
  purchase: Pick<PurchaseApprovalPayload, 'totalSource' | 'linesReadable'>,
): string | null => {
  const problems: string[] = [];
  if (purchase.totalSource === 'heuristic') {
    problems.push(
      'Dream could not read this total exactly — it was taken from the page layout.',
    );
  }
  if (!purchase.linesReadable) {
    problems.push(
      'Dream could not read the basket lines — the single line above was taken from the total.',
    );
  }
  if (problems.length === 0) return null;
  return `${problems.join(' ')} Check the screenshot before approving.`;
};

/** The opaque choice posted back to `/secure-payments/mandates/:id/approve`. */
export interface PurchaseInstrumentChoice {
  type: Exclude<PurchaseInstrumentType, 'new_card'>;
  ref?: string | null;
}

/**
 * Body of the approve POST. `securityCode` is present only for a saved card
 * whose code is not on file; the server seals it for the executor and never
 * echoes it back. It lives in component state for the life of the sheet and
 * nowhere else — not the artifact, not the thread.
 */
export interface PurchaseApprovalRequest {
  threadId: string;
  instrument: PurchaseInstrumentChoice;
  securityCode?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isLine = (value: unknown): value is PurchaseApprovalLine =>
  isRecord(value) &&
  typeof value.description === 'string' &&
  typeof value.quantity === 'number';

const INSTRUMENT_TYPES: ReadonlySet<string> = new Set<PurchaseInstrumentType>([
  'agent_card',
  'user_card',
  'new_card',
  'merchant_stored',
]);

const isInstrumentOption = (
  value: unknown,
): value is PurchaseInstrumentOption =>
  isRecord(value) &&
  typeof value.type === 'string' &&
  INSTRUMENT_TYPES.has(value.type);

/**
 * Instrument options as the server sent them, reduced to the display fields
 * the client is allowed to know. Anything that is not a recognised option is
 * dropped; anything on an option beyond these fields is never read.
 */
export const normaliseInstrumentOptions = (
  raw: unknown,
): PurchaseInstrumentOption[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isInstrumentOption).map((option) => ({
    type: option.type,
    ref: typeof option.ref === 'string' ? option.ref : null,
    label: typeof option.label === 'string' ? option.label : null,
    last4: typeof option.last4 === 'string' ? option.last4 : null,
    network: typeof option.network === 'string' ? option.network : null,
    cvcOnFile:
      option.type === 'user_card' && typeof option.cvcOnFile === 'boolean'
        ? option.cvcOnFile
        : null,
  }));
};

export const MANDATE_APPROVAL_VARIANT = 'mandate_as_approval';

/**
 * The spending-approval card carrying the secure card picker: the user's
 * ceiling approval is also the payment approval, and the card chosen here is
 * the one Dream pays with at the checkout. Present only when the server put
 * it on the artifact — the client has no flag of its own and shows the
 * legacy sheet whenever this is absent.
 */
export interface MandateSecurePayment {
  variant: typeof MANDATE_APPROVAL_VARIANT;
  instruments: PurchaseInstrumentOption[];
  /** A saved card without its code on file needs the code typed on the card. */
  cvcRequired: boolean;
  /** Saved card to preselect: the one the replaced approval used. */
  preferredRef?: string | null;
}

export const getMandateSecurePayment = (
  artifact: ResponseArtifactDTO,
): MandateSecurePayment | null => {
  if (artifact.artifactType !== 'spending_approval') return null;
  const block: unknown = (artifact as { securePayment?: unknown })
    .securePayment;
  if (!isRecord(block) || block.variant !== MANDATE_APPROVAL_VARIANT) {
    return null;
  }
  return {
    variant: MANDATE_APPROVAL_VARIANT,
    instruments: normaliseInstrumentOptions(block.instruments),
    cvcRequired: block.cvcRequired !== false,
    preferredRef:
      typeof block.preferredRef === 'string' ? block.preferredRef : null,
  };
};

/**
 * Typed view of a spending-approval artifact's `purchase` payload, or null
 * for the legacy cap-style approvals (and for public artifact projections,
 * which never carry it).
 */
export const getPurchaseApproval = (
  artifact: ResponseArtifactDTO,
): PurchaseApprovalPayload | null => {
  if (artifact.artifactType !== 'spending_approval') return null;
  const purchase: unknown = (artifact as { purchase?: unknown }).purchase;
  if (!isRecord(purchase)) return null;
  if (
    typeof purchase.purchaseMandateId !== 'string' ||
    typeof purchase.secureRequestId !== 'string' ||
    typeof purchase.merchantOrigin !== 'string' ||
    typeof purchase.amount !== 'number' ||
    typeof purchase.currency !== 'string' ||
    typeof purchase.approvalDeadlineAt !== 'string' ||
    !Array.isArray(purchase.lines) ||
    !Array.isArray(purchase.instrumentOptions)
  ) {
    return null;
  }
  const source: unknown = purchase.deadlineSource;
  const deadlineSource: PurchaseDeadlineSource =
    source === 'site_timer' || source === 'judge_estimate' ? source : 'default';
  return {
    purchaseMandateId: purchase.purchaseMandateId,
    secureRequestId: purchase.secureRequestId,
    merchantOrigin: purchase.merchantOrigin,
    amount: purchase.amount,
    currency: purchase.currency,
    lines: purchase.lines.filter(isLine),
    approvalDeadlineAt: purchase.approvalDeadlineAt,
    deadlineSource,
    deadlineTooShort: purchase.deadlineTooShort === true,
    evidenceArtifactId:
      typeof purchase.evidenceArtifactId === 'string'
        ? purchase.evidenceArtifactId
        : null,
    instrumentOptions: normaliseInstrumentOptions(purchase.instrumentOptions),
    surchargeNotice:
      typeof purchase.surchargeNotice === 'string'
        ? purchase.surchargeNotice
        : '',
    totalSource:
      purchase.totalSource === 'exact' || purchase.totalSource === 'heuristic'
        ? purchase.totalSource
        : null,
    linesReadable: purchase.linesReadable !== false,
    approveEndpoint:
      typeof purchase.approveEndpoint === 'string'
        ? purchase.approveEndpoint
        : `/secure-payments/mandates/${purchase.purchaseMandateId}`,
  };
};

/**
 * `payment_cvv` / `payment_3ds` for the private code forms, `payment_3ds_live`
 * for the owner-only live bank challenge, else null.
 */
export const paymentCredentialKind = (
  artifact: ResponseArtifactDTO,
): PaymentCredentialKind | null => {
  const kind: unknown = (artifact as { credentialKind?: unknown })
    .credentialKind;
  return typeof kind === 'string' && PAYMENT_CREDENTIAL_KINDS.has(kind)
    ? (kind as PaymentCredentialKind)
    : null;
};

/** True for the live bank challenge sheet (no code field, live view instead). */
export const isLiveChallengeArtifact = (
  artifact: ResponseArtifactDTO,
): boolean => paymentCredentialKind(artifact) === 'payment_3ds_live';

/**
 * True for any private-only artifact other than the purchase approval card:
 * payment code forms, the live bank challenge and any other card flagged
 * `privateOnly`. These must never be shown outside the owner's own
 * authenticated Dream chat.
 */
export const isPrivateOnlyPaymentArtifact = (
  artifact: ResponseArtifactDTO,
): boolean =>
  getPurchaseApproval(artifact) === null &&
  (paymentCredentialKind(artifact) !== null ||
    (artifact as { privateOnly?: unknown }).privateOnly === true);
